import {
  Stack,
  Paper,
  Container,
  Box,
  Typography,
  useMediaQuery,
  Grid,
} from "@mui/material";
import ConsumptionsBillsSearchBar from "../../ConsumptionsBillsSearchBar/ConsumptionsBillsSearchBar";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { useEffect, useRef, useState } from "react";
import {
  ConsumptionListData,
  ConsumptionsPeriodInformation,
  GetGraphDataRequestBody,
} from "../../../core/models/consumptions/consumptions.model";
import { UserData } from "../../../core/models/login/login.model";
import { getGraphDataBills } from "../../../core/services/bills/bills.service";
import {
  getBarChart,
  getBarPositionX,
  handleClickBarChart,
} from "../../../utils/components/consumptions/consumptions.util";
import { formatStringDate } from "../../../utils/dateUtils/formatDate.util";
import { formatNumber } from "../../../utils/formatters/formatters.util";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import EmptyStateComponent from "../../UI/EmptyStateComponent/EmptyStateComponent";
import theme from "../../../muiTheme";
import { Chart, ActiveElement, ChartEvent } from "chart.js";
import {
  formatBillsConsumptions,
  getDatasets,
} from "../../../utils/components/bills/bills.util";
import {
  BillsConsumptionsFormatted,
  ConsumptionBillsData,
  SelectedDataPeriodBills,
} from "../../../core/models/bills/bills.model";
import "./BillsEvolution.scss";
import { t } from "i18next";
import { SearchParams } from "../../../core/models/consumptionsBillsSearchBar/consumptionsBillsSearchBar.model";
import { getGraphDataPeriodConsumptions } from "../../../core/services/consumptions/consumptions.service";
import { GroupedSuppliesTableRow } from "../../../core/models/supplies/supplies.model";

export interface BillsEvolutionProps {
  currentCups: GroupedSuppliesTableRow | null;
  ownerDataCups: OwnerDataCups[];
  userData: UserData | null;
  searchParams: SearchParams;
  setSearchParams: React.Dispatch<React.SetStateAction<SearchParams>>;
  selectedDataPeriod: SelectedDataPeriodBills;
  setSelectedDataPeriod: React.Dispatch<
    React.SetStateAction<SelectedDataPeriodBills>
  >;
  consumptionsList: BillsConsumptionsFormatted[];
  setConsumptionsList: React.Dispatch<
    React.SetStateAction<BillsConsumptionsFormatted[]>
  >;
  consumptionsBillsPeriodInformation: ConsumptionsPeriodInformation;
  setConsumptionsBillsPeriodInformation: React.Dispatch<
    React.SetStateAction<ConsumptionsPeriodInformation>
  >;
}

const BillsEvolution = ({
  currentCups,
  ownerDataCups,
  userData,
  searchParams,
  setSearchParams,
  selectedDataPeriod: { positionX, consumptionBill, elements },
  setSelectedDataPeriod,
  consumptionsList,
  setConsumptionsList,
  consumptionsBillsPeriodInformation,
  setConsumptionsBillsPeriodInformation,
}: BillsEvolutionProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFirstChartLoadFlag, setIsFirstChartLoadFlag] =
    useState<boolean>(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const environment = "Gas";
  const isDesktopViewport = useMediaQuery("(min-width: 1100px)");
  const chart = useRef<Chart | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      chart.current = getBarChart(
        environment,
        canvasRef.current,
        consumptionsList,
        isDesktopViewport,
        (elements: ActiveElement[], event: ChartEvent) => {
          handleClickBarChart(elements, chart.current as Chart);
          setSelectedDataPeriod({
            consumptionBill: consumptionsList[elements[0].index],
            positionX: getBarPositionX(chart.current as Chart, event),
            elements,
          });
        },
        getDatasets(consumptionsList),
        false
      );
      return () => {
        (chart.current as Chart).destroy();
      };
    }
  }, [consumptionsList, isDesktopViewport, setSelectedDataPeriod]);

  useEffect(() => {
    if (isFirstChartLoadFlag && chart.current && elements.length) {
      handleClickBarChart(elements, chart.current as Chart);
      setIsFirstChartLoadFlag(false);
    }
  }, [elements, isFirstChartLoadFlag]);

  const consumptionListData: ConsumptionListData[] = [
    {
      id: "selectedPeriod",
      label: "Periodo seleccionado",
      value: `${consumptionsBillsPeriodInformation.dateFrom} - ${consumptionsBillsPeriodInformation.dateTo}`,
    },
    {
      id: "totalConsumedEnergy",
      label: "Total de energía consumida",
      value: `${consumptionsBillsPeriodInformation.totalConsumedEnergy} kWh`,
    },
    {
      id: "averageExpense",
      label: "Gasto medio",
      value: `${consumptionsBillsPeriodInformation.averageExpense}€`,
    },
  ];

  const consumptionBillsData: ConsumptionBillsData[] = [
    {
      id: "billsDate",
      label: "Fecha de factura",
      value: `${consumptionBill?.invoiceDate}`,
    },
    {
      id: "period",
      label: "Periodo facturado",
      value: t("Consumos.Desde al", {
        dateFrom: consumptionBill?.dateFrom,
        dateTo: consumptionBill?.dateTo,
        interpolation: { escapeValue: false },
      }),
    },
  ];

  const handleClickSearch = (searchParams: GetGraphDataRequestBody) => {
    setIsLoading(true);
    Promise.all([
      getGraphDataBills(searchParams, userData?.token || ""),
      getGraphDataPeriodConsumptions(searchParams, userData?.token || ""),
    ])
      .then(([{ gastoMedio, consumptionList }, { totalEnergiaConsumida }]) => {
        setConsumptionsList(formatBillsConsumptions(consumptionList));
        setConsumptionsBillsPeriodInformation({
          averageExpense: formatNumber(+gastoMedio.toFixed(2)),
          totalConsumedEnergy: formatNumber(totalEnergiaConsumida),
          dateFrom: formatStringDate(searchParams.dateFrom),
          dateTo: formatStringDate(searchParams.dateTo),
        });
        setSelectedDataPeriod((prevSelectedDataPeriod) => ({
          ...prevSelectedDataPeriod,
          consumptionBill: null,
        }));
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  };

  return (
    <Container component="article">
      <Stack component="article" rowGap={"2rem"} padding={"2rem 0"}>
        <ConsumptionsBillsSearchBar
          currentCup={currentCups}
          ownerDataCups={ownerDataCups}
          handleClickSearch={handleClickSearch}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          isLoading={isLoading}
        />
        {!isLoading ? (
          <>
            {!!consumptionsList.length ? (
              <Paper sx={{ width: "100%", borderRadius: "12px" }}>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexWrap="wrap"
                  borderBottom={`solid 2px ${theme.palette.grey[100]}`}
                  paddingX="2rem"
                  paddingY="1.5rem"
                  gap="4rem"
                >
                  {consumptionListData.map(({ id, label, value }) => (
                    <Stack direction="column" gap="0.5rem" key={id}>
                      <Typography
                        component="p"
                        variant="body2"
                        sx={{ color: "grey.500" }}
                      >
                        {t(`Consumos.${label}`)}
                      </Typography>
                      <Typography component="h3" variant="subtitle1">
                        {value}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
                <Stack component="div">
                  <Box paddingTop={"4rem"} paddingBottom={"2rem"}>
                    <canvas
                      id="chart"
                      ref={canvasRef}
                      style={{ height: "600px" }}
                    />
                  </Box>
                  <Stack
                    component="div"
                    height={"auto"}
                    bgcolor={theme.palette.grey[50]}
                    border={`1px solid ${theme.palette.grey[300]}`}
                    borderRadius={"12px"}
                    margin={"3rem"}
                    paddingY={"3rem"}
                    paddingX={{ xs: "1.5rem", md: "6rem" }}
                    position={"relative"}
                    display={"flex"}
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    gap={"1rem"}
                  >
                    {consumptionBill ? (
                      <>
                        <Box
                          component={"div"}
                          position={"absolute"}
                          bgcolor={theme.palette.grey[50]}
                          sx={{
                            borderTop: `1px solid ${theme.palette.grey[300]}`,
                            borderLeft: `1px solid ${theme.palette.grey[300]}`,
                          }}
                          width={"20px"}
                          height={"20px"}
                          top={-11}
                          left={positionX ? positionX - 60 : 0}
                          className="arrow-graph"
                        />
                        <Grid
                          container
                          flexDirection={"column"}
                          spacing="1.5rem"
                        >
                          {consumptionBillsData.map(({ id, label, value }) => (
                            <Grid item key={id}>
                              <Typography
                                component={"p"}
                                variant="body1"
                                color={theme.palette.grey[500]}
                                paddingBottom="0.25rem"
                              >
                                {t(`Consumos.${label}`)}
                              </Typography>
                              <Typography component={"p"} variant="subtitle1">
                                {value}
                              </Typography>
                            </Grid>
                          ))}
                        </Grid>
                        <Grid container flexDirection={"column"}>
                          <Grid item textAlign={{ xs: "left", md: "right" }}>
                            <Typography
                              component={"p"}
                              variant="body1"
                              color={theme.palette.grey[500]}
                            >
                              {t("Consumos.Importe")}
                            </Typography>
                            <Typography component={"p"} variant="h1">
                              {`${formatNumber(consumptionBill.amount)}€`}
                            </Typography>
                          </Grid>
                        </Grid>
                      </>
                    ) : (
                      <Stack
                        width={"100%"}
                        display={"flex"}
                        flex-direction={"column"}
                        alignContent={"center"}
                        alignItems={"center"}
                        rowGap={2}
                      >
                        <Typography
                          component={"span"}
                          variant="h3"
                          className="t-icon-select round-icon"
                        ></Typography>
                        <Typography
                          component={"p"}
                          variant={"subtitle2"}
                          color={theme.palette.grey[700]}
                          margin={"auto"}
                        >
                          {t(
                            `Consumos.Selecciona una columna para consultar el detalle de consumo`
                          )}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            ) : (
              <EmptyStateComponent
                text={t(
                  "Consumos.Selecciona un suministro y un periodo para mostrar consumos"
                )}
                height={"30rem"}
              />
            )}
          </>
        ) : (
          <Paper
            sx={{
              width: "100%",
              borderRadius: "12px",
              height: "30rem",
              position: "relative",
            }}
          >
            <LoadingComponentComponent isLoading={isLoading} />
          </Paper>
        )}
      </Stack>
    </Container>
  );
};

export default BillsEvolution;
